import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import {
  ArrowRight, ShieldCheck, Sprout, BrainCircuit, Radar, Scale, Gauge, Wind,
  ThermometerSun, Network, FileCheck2, Landmark, Bot, Leaf, Globe2, Building2,
  Users, Database, Recycle, Target, GitBranch, CheckCircle2, MapPin, Mail,
  Globe, Layers, Compass, ScrollText, BadgeCheck, ChevronRight, Quote,
} from "lucide-react";

const INK = "#0F3D3A";
const TEAL = "#117A7A";
const GOLD = "#D4A84C";
const serif = { fontFamily: "Georgia, 'Times New Roman', serif" };

const HERO_IMG = "https://images.unsplash.com/photo-1508964942454-1a56651d54ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxTaW5nYXBvcmUlMjBza3lsaW5lJTIwZ3JlZW4lMjBzdXN0YWluYWJpbGl0eXxlbnwwfHx8fDE3OTAxNzU5NzV8MA&ixlib=rb-4.1.0&q=85";
const TEAM_IMG = "https://images.pexels.com/photos/8102000/pexels-photo-8102000.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CITY_IMG = "https://images.unsplash.com/photo-1496939376851-89342e90adcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxTaW5nYXBvcmUlMjBza3lsaW5lJTIwZ3JlZW4lMjBzdXN0YWluYWJpbGl0eXxlbnwwfHx8fDE3OTAxNzU5NzV8MA&ixlib=rb-4.1.0&q=85";
const LESTARI_IMG = "https://images.pexels.com/photos/36810327/pexels-photo-36810327.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUE_FLOW = ["Data", "Insight", "Decision", "Action", "Resilience"];
const DATA_FLOW = ["Collect Once", "Validate", "Map", "Reuse", "Report"];

const ASIAN_QUESTIONS = [
  { icon: Target, q: "How do we remain competitive?" },
  { icon: ShieldCheck, q: "How do we protect our operations?" },
  { icon: Network, q: "How do we strengthen our supply chain?" },
  { icon: ThermometerSun, q: "How do we prepare for climate disruption?" },
  { icon: Landmark, q: "How do we finance the transition?" },
  { icon: Scale, q: "How do we turn sustainability into measurable business value?" },
];

const CONNECTIONS = [
  { icon: Wind, label: "Emissions" },
  { icon: Recycle, label: "Resources" },
  { icon: Gauge, label: "Operations" },
  { icon: Network, label: "Suppliers" },
  { icon: ThermometerSun, label: "Climate Risks" },
  { icon: Landmark, label: "Investments" },
  { icon: FileCheck2, label: "Compliance Obligations" },
  { icon: Scale, label: "Financial Opportunities" },
];

const STANDARD_GROUPS = [
  {
    region: "Singapore & Local",
    blurb: "Designed with Singapore's evolving reporting and climate-disclosure environment in mind.",
    items: [
      { code: "SGX Sustainability Reporting", body: "Collect and organise material ESG factors, policies, performance, targets, climate-related disclosures and governance — referencing IFRS S1 and IFRS S2." },
      { code: "ACRA & SG Climate Reporting", body: "Architected around Singapore's climate reporting and assurance roadmap, including ISSB-based disclosures being phased in for relevant organisations." },
      { code: "SGX Core ESG Metrics", body: "Structured ESG data mapped to commonly requested environmental, social and governance indicators — useful reference points for listed and non-listed organisations alike." },
    ],
  },
  {
    region: "Industry Requirements",
    blurb: "Sector and scheme-specific checklists organisations are increasingly asked to evidence.",
    items: [
      { code: "BCA Green Mark", body: "Singapore's green-building rating scheme — capture energy, water, materials and environmental performance data relevant to Green Mark certification." },
      { code: "MSC — Mandai Sustainability Checklist", body: "Organise the sustainability information and criteria expected under Mandai's precinct sustainability checklist for developments and tenants." },
      { code: "FSC — Forest Stewardship Council", body: "Support responsible-sourcing and chain-of-custody evidence for forestry and wood-based materials against FSC checklist requirements." },
      { code: "CBAM Compliance", body: "Structure embedded-emissions and product data for the EU Carbon Border Adjustment Mechanism reporting that exporters increasingly face." },
    ],
  },
  {
    region: "ASEAN Alignment",
    blurb: "Built for organisations operating across Southeast Asia, where requirements extend across borders.",
    items: [
      { code: "ASEAN Taxonomy for Sustainable Finance", body: "A regional classification framework Lestari can reference when organisations evaluate transition and sustainable-finance opportunities." },
      { code: "ASEAN Sustainable Finance Standards", body: "Information structures aligned to ASEAN Green, Social and Sustainability Bond Standards and related sustainable-finance guidance." },
    ],
  },
  {
    region: "International Standards",
    blurb: "Data, analytics and reporting architecture designed with reference to widely recognised frameworks.",
    items: [
      { code: "IFRS S1", body: "General requirements for disclosure of sustainability-related financial information — governance, strategy, risk management, metrics and targets." },
      { code: "IFRS S2", body: "Climate-related disclosures covering physical and transition risks, incorporating the four-pillar TCFD architecture and SASB industry guidance." },
      { code: "GRI Standards", body: "Impact-oriented reporting across economic, environmental and social topics through Universal, Sector and Topic Standards." },
      { code: "GHG Protocol", body: "Organisational greenhouse gas accounting across Scope 1, Scope 2 and relevant Scope 3 value-chain emissions." },
      { code: "ISO 14064-1", body: "A reference point for organisational-level quantification and reporting of greenhouse gas emissions and removals." },
      { code: "ISO 14067", body: "Product-level carbon footprints and life-cycle-based carbon information." },
      { code: "TNFD", body: "A reference architecture for nature-related dependencies, impacts, risks and opportunities." },
      { code: "UN SDGs", body: "Connecting sustainability initiatives to wider economic, social and environmental development objectives." },
    ],
  },
];

const ISO14001_ITEMS = [
  "Environmental aspects and impacts",
  "Energy, emissions, water and waste performance",
  "Environmental objectives, targets and KPIs",
  "Environmental risks and opportunities",
  "Applicable obligations and internal controls",
  "Initiatives and improvement programmes",
  "Performance monitoring and management dashboards",
  "Evidence, corrective actions and continual improvement",
];

const REUSE_EXAMPLES = [
  { source: "A verified electricity record", chain: ["GHG accounting", "environmental KPIs", "climate disclosure", "customer questionnaires", "ESG reports", "ISO 14001 evidence"] },
  { source: "A supplier record", chain: ["Scope 3", "supply-chain risk", "human-rights assessment", "CBAM", "customer compliance", "procurement"] },
  { source: "A sustainability investment", chain: ["emissions reduction", "operational savings", "resilience", "targets", "green-financing"] },
];

const EVOLVE_ITEMS = ["Data models", "Metrics", "Calculations", "Workflows", "Reporting structures", "AI guidance", "Evidence requirements", "Compliance mappings"];

const CAPABILITIES = [
  { icon: Gauge, title: "Sustainability Management", body: "Measure environmental, social and governance performance and understand what matters most to the organisation." },
  { icon: Wind, title: "Carbon & Resource Intelligence", body: "Track emissions, energy, water, waste and other operational sustainability indicators." },
  { icon: ThermometerSun, title: "Climate Risk & Resilience", body: "Understand exposure to climate hazards and build resilience into business planning." },
  { icon: Network, title: "Supply Chain Sustainability", body: "Engage suppliers, assess performance, understand risks and improve value-chain visibility." },
  { icon: FileCheck2, title: "Reporting & Compliance", body: "Transform structured data into credible disclosures aligned with relevant local and international requirements." },
  { icon: Landmark, title: "Investment & Financing", body: "Evaluate initiatives, understand returns and explore sustainability-linked funding opportunities." },
  { icon: BrainCircuit, title: "AI-Powered Decision Support", body: "Turn sustainability information into insights, recommendations and practical actions leaders can understand." },
];

const INFLUENCE_CHAIN = [
  "Customers influence suppliers",
  "Banks influence businesses",
  "Large companies influence value chains",
  "Communities influence industries",
  "Industries influence economies",
];

/* ---------- small building blocks ---------- */
function Eyebrow({ children, onDark }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: onDark ? GOLD : TEAL }}>
      {children}
    </div>
  );
}

function FlowBar({ steps, onDark, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <span
            className="inline-flex items-center rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-semibold"
            style={
              i === steps.length - 1
                ? { background: GOLD, color: INK }
                : onDark
                ? { background: "rgba(255,255,255,0.08)", color: "#F2F4F3", border: "1px solid rgba(255,255,255,0.14)" }
                : { background: "#fff", color: INK, border: `1px solid ${INK}1a` }
            }
          >
            {s}
          </span>
          {i < steps.length - 1 && (
            <ChevronRight className="h-4 w-4 shrink-0" style={{ color: onDark ? GOLD : TEAL }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ---------- page ---------- */
export default function About() {
  const navigate = useNavigate();
  const go = () => navigate("/get-started");
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#F2F4F3] text-[#0F3D3A]" data-testid="about-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-[#0F3D3A]/10 bg-[#F2F4F3]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center" data-testid="about-brand-home">
            <img src="/brand/logo_horizontal.png" alt="Lestari" className="h-9 object-contain" />
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/about" className="text-sm font-semibold text-[#0F3D3A] transition-colors hidden sm:block" data-testid="about-nav-about">About</Link>
            <Link to="/why-lestari" className="text-sm text-[#0F3D3A]/70 hover:text-[#0F3D3A] transition-colors hidden sm:block" data-testid="about-nav-why">Why Lestari</Link>
            <Link to="/login" className="text-sm text-[#0F3D3A]/70 hover:text-[#0F3D3A] transition-colors" data-testid="about-nav-signin">Sign in</Link>
            <button onClick={go} data-testid="about-nav-trial"
              className="inline-flex items-center gap-1.5 bg-[#0F3D3A] hover:bg-[#117A7A] text-[#F2F4F3] text-sm font-semibold px-4 py-2 rounded-full transition-colors">
              Start free trial <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        <div className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full opacity-[0.07]" style={{ background: `radial-gradient(circle, ${TEAL}, transparent 70%)` }} />
        <div className="max-w-6xl mx-auto px-6 pt-14 pb-16 lg:pt-20 lg:pb-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <Eyebrow>About Lestari · Sustainability Intelligence</Eyebrow>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.05]" style={serif} data-testid="about-hero-title">
              Resilience<br /><span style={{ color: GOLD }}>in Action.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg max-w-xl leading-relaxed text-[#0F3D3A]/75" data-testid="about-hero-lede">
              Lestari is a <span className="font-semibold text-[#0F3D3A]">Sustainability &amp; Resilience Engine</span> built to help
              Asian organisations turn sustainability from a reporting obligation into a practical business advantage.
            </p>
            <p className="mt-4 text-sm max-w-xl leading-relaxed text-[#0F3D3A]/65">
              Because sustainability should not only tell an organisation what happened — it should help leaders decide what to do next.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={go} data-testid="about-hero-cta"
                className="inline-flex items-center gap-2 bg-[#0F3D3A] hover:bg-[#117A7A] text-[#F2F4F3] font-semibold px-6 py-3 rounded-full transition-colors shadow-lg">
                <Leaf className="h-4 w-4" /> Join the network <ArrowRight className="h-4 w-4" />
              </button>
              <Link to="/why-lestari" className="text-sm text-[#117A7A] hover:text-[#0F3D3A] font-medium transition-colors" data-testid="about-hero-why">
                Why Lestari →
              </Link>
            </div>
          </div>
          <div className="relative">
            <img src={HERO_IMG} alt="Sustainable Singapore" loading="eager"
              className="w-full h-[340px] lg:h-[420px] object-cover rounded-2xl border border-[#0F3D3A]/10 shadow-xl" data-testid="about-hero-img" />
            <div className="absolute -bottom-4 left-4 right-4 rounded-xl bg-[#0F3D3A] text-[#F2F4F3] px-5 py-3 shadow-lg flex items-center gap-3">
              <Globe2 className="h-4 w-4" style={{ color: GOLD }} />
              <span className="text-xs sm:text-sm">The Sustainability &amp; Resilience Engine for Asia</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-white border-t border-[#0F3D3A]/10" data-testid="about-mission">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <Eyebrow>Our mission</Eyebrow>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight max-w-4xl leading-tight" style={serif}>
            Make intelligent sustainability and resilience technology affordable, practical and accessible to
            organisations across Asia.
          </h2>
          <p className="text-[#0F3D3A]/70 text-base mt-5 max-w-3xl leading-relaxed">
            Regardless of size, industry or level of sustainability maturity. Lestari brings sustainability data,
            climate intelligence, compliance support, operational performance, supply-chain visibility, risk management
            and financing insights together in one intelligent platform.
          </p>
          <div className="mt-9 rounded-2xl border border-[#0F3D3A]/10 bg-[#F2F4F3] p-6 lg:p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0F3D3A]/50 mb-4">Lestari helps organisations move from</div>
            <FlowBar steps={VALUE_FLOW} testid="about-value-flow" />
          </div>
        </div>
      </section>

      {/* Built by practitioners */}
      <section className="border-t border-[#0F3D3A]/10" data-testid="about-practitioners">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <Eyebrow>Built by practitioners</Eyebrow>
            <h2 className="text-3xl font-bold tracking-tight" style={serif}>Designed for real businesses.</h2>
            <p className="text-[#0F3D3A]/70 text-sm mt-5 leading-relaxed">
              Lestari was developed by a group of sustainability, technology and business practitioners — led by a former
              Microsoft Sustainability Architect — with experience helping organisations navigate the practical
              challenges of sustainability transformation, ESG data management, climate risk, reporting and digital
              innovation.
            </p>
            <p className="text-[#0F3D3A]/70 text-sm mt-4 leading-relaxed">
              We saw a recurring problem. Many organisations understand that sustainability is becoming essential, but
              the technologies, consulting resources and expertise required can be expensive, fragmented and difficult to
              navigate. For many SMEs and growing organisations, sustainability quickly becomes a maze of spreadsheets,
              questionnaires, standards, carbon calculations, customer requests and compliance deadlines.
            </p>
            <div className="mt-6 rounded-xl bg-[#0F3D3A] text-[#F2F4F3] p-5 border-l-4" style={{ borderLeftColor: GOLD }}>
              <p className="text-sm leading-relaxed">
                We built Lestari to change that — giving organisations the intelligence, automation and decision-support
                once available only to large enterprises, at a scale and cost businesses across Asia can realistically
                adopt.
              </p>
            </div>
          </div>
          <div>
            <img src={TEAM_IMG} alt="Practitioners collaborating" loading="lazy"
              className="w-full h-[300px] lg:h-[380px] object-cover rounded-2xl border border-[#0F3D3A]/10 shadow-lg" data-testid="about-team-img" />
            <p className="text-[11px] font-mono text-[#0F3D3A]/50 mt-3">
              Lestari AI Platform — a wholly owned intellectual property of SusTek Pte Ltd, developed and continuously
              enhanced by SusTek.
            </p>
          </div>
        </div>
      </section>

      {/* Why Lestari — the widening conversation */}
      <section className="bg-white border-t border-[#0F3D3A]/10" data-testid="about-why">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <Eyebrow>Why Lestari</Eyebrow>
          <h2 className="text-3xl font-bold tracking-tight max-w-3xl" style={serif}>
            Sustainability is changing. It is moving beyond carbon accounting and annual reports.
          </h2>
          <p className="text-[#0F3D3A]/65 text-sm mt-3 max-w-3xl leading-relaxed">
            Businesses are increasingly asked to understand how climate risks affect operations and assets, how resilient
            their suppliers are, how environmental performance affects costs, how customer and regulatory requirements are
            changing — and where sustainability investments deliver measurable returns. Lestari connects these questions
            within one platform.
          </p>
        </div>
      </section>

      {/* Built for the Asian reality (dark band) */}
      <section className="bg-[#0F3D3A] text-[#F2F4F3]" data-testid="about-asia">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div>
            <Eyebrow onDark>Built for the Asian reality</Eyebrow>
            <h2 className="text-3xl font-bold tracking-tight" style={serif}>The question is no longer only "how do we comply?"</h2>
            <p className="text-[#F2F4F3]/70 text-sm mt-4 leading-relaxed max-w-md">
              Asia sits at the centre of some of the world's fastest-growing economies — while facing significant exposure
              to climate hazards, complex supply chains, resource constraints and rapidly evolving requirements. Rather
              than treating sustainability, compliance, climate risk and operational performance as separate initiatives,
              Lestari brings them together as part of a company's broader resilience strategy.
            </p>
            <img src={CITY_IMG} alt="Asian city skyline" loading="lazy"
              className="mt-7 w-full h-[220px] object-cover rounded-2xl border border-white/10" data-testid="about-asia-img" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="about-asia-questions">
            {ASIAN_QUESTIONS.map((a) => (
              <div key={a.q} className="rounded-xl bg-white/[0.06] border border-white/10 p-4" data-testid={`about-asia-q-${a.q.slice(0, 12).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <a.icon className="h-4.5 w-4.5 mb-2" style={{ color: GOLD }} />
                <div className="text-sm font-medium leading-snug">{a.q}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* From compliance to resilience */}
      <section className="border-t border-[#0F3D3A]/10" data-testid="about-compliance">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <Eyebrow>From compliance to resilience</Eyebrow>
          <h2 className="text-3xl font-bold tracking-tight max-w-3xl" style={serif}>
            A platform should do more than generate a report.
          </h2>
          <p className="text-[#0F3D3A]/65 text-sm mt-3 max-w-3xl leading-relaxed">
            Lestari helps organisations understand the connections between everything that shapes their resilience — so
            data becomes something leaders can use for decisions, not just something collected for compliance.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-9" data-testid="about-connections">
            {CONNECTIONS.map((c) => (
              <div key={c.label} className="rounded-xl border border-[#0F3D3A]/10 bg-white p-4 flex flex-col items-start gap-2 hover:shadow-md transition-shadow">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: "#117A7A14", border: "1px solid #117A7A33" }}>
                  <c.icon className="h-4.5 w-4.5" style={{ color: TEAL }} />
                </div>
                <div className="text-sm font-semibold leading-snug">{c.label}</div>
              </div>
            ))}
          </div>
          <p className="text-[#0F3D3A]/55 text-xs mt-5 max-w-3xl leading-relaxed font-mono">
            With AI-assisted analytics and a connected data foundation, organisations can identify risks earlier, uncover
            inefficiencies, model interventions and build a clearer pathway towards resilience.
          </p>
        </div>
      </section>

      {/* Standards & frameworks */}
      <section className="bg-white border-t border-[#0F3D3A]/10" data-testid="about-standards">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <Eyebrow>Standards, frameworks &amp; regulatory alignment</Eyebrow>
          <h2 className="text-3xl font-bold tracking-tight max-w-3xl" style={serif}>Designed around recognised requirements.</h2>
          <p className="text-[#0F3D3A]/65 text-sm mt-3 max-w-3xl leading-relaxed">
            Lestari is architected around the data structures, principles, metrics and disclosure requirements of relevant
            local, ASEAN and international standards. Establish a stronger sustainability data foundation once — then use
            it to support many reporting, customer, regulatory, financing and certification-readiness requirements.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mt-10 items-start" data-testid="about-standard-groups">
            {STANDARD_GROUPS.map((g) => (
              <div key={g.region} className="rounded-2xl border border-[#0F3D3A]/10 bg-[#F2F4F3] p-6" data-testid={`about-standard-${g.region.slice(0, 8).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Globe className="h-4 w-4" style={{ color: TEAL }} />
                  <div className="font-bold text-base" style={serif}>{g.region}</div>
                </div>
                <p className="text-xs text-[#0F3D3A]/60 leading-relaxed">{g.blurb}</p>
                <div className="mt-4 space-y-3">
                  {g.items.map((it) => (
                    <div key={it.code} className="border-t border-[#0F3D3A]/10 pt-3">
                      <div className="font-mono text-[11px] font-semibold" style={{ color: INK }}>{it.code}</div>
                      <p className="text-xs text-[#0F3D3A]/60 mt-1 leading-relaxed">{it.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* ISO 14001 readiness */}
          <div className="mt-8 rounded-2xl border border-[#0F3D3A]/10 bg-[#0F3D3A] text-[#F2F4F3] p-6 lg:p-8" data-testid="about-iso14001">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <BadgeCheck className="h-5 w-5" style={{ color: GOLD }} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: GOLD }}>Certification readiness</span>
                </div>
                <h3 className="text-2xl font-bold tracking-tight" style={serif}>Supporting the journey towards ISO 14001.</h3>
                <p className="text-[#F2F4F3]/70 text-sm mt-3 leading-relaxed">
                  Lestari can serve as a digital foundation for environmental management and certification readiness —
                  improving evidence quality and management visibility. Using Lestari does not, by itself, make an
                  organisation ISO 14001 certified: certification requires the organisation to operate the applicable
                  management system and undergo independent conformity assessment.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {ISO14001_ITEMS.map((x) => (
                  <div key={x} className="flex items-start gap-2 text-xs text-[#F2F4F3]/80 rounded-lg bg-white/[0.05] border border-white/10 p-3">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: GOLD }} /> {x}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we mean by aligned — credibility note */}
      <section className="border-t border-[#0F3D3A]/10" data-testid="about-aligned">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <Eyebrow>What we mean by "aligned"</Eyebrow>
            <h2 className="text-3xl font-bold tracking-tight" style={serif}>Alignment is not accreditation.</h2>
          </div>
          <div className="mt-6 rounded-2xl bg-white border border-[#0F3D3A]/10 p-6 lg:p-8 border-l-4 max-w-4xl" style={{ borderLeftColor: GOLD }} data-testid="about-aligned-note">
            <Quote className="h-6 w-6 mb-3" style={{ color: GOLD }} />
            <p className="text-sm text-[#0F3D3A]/75 leading-relaxed">
              When we say Lestari is aligned with, designed around, mapped to or takes reference from a standard, we mean
              that relevant elements of the platform help organisations capture, manage, analyse or report information
              associated with those requirements.
            </p>
            <p className="text-sm text-[#0F3D3A]/75 leading-relaxed mt-4">
              Unless explicitly stated otherwise, Lestari and SusTek do not claim to be accredited, certified, endorsed or
              officially affiliated with the organisations that publish or administer these standards. Nor does using
              Lestari automatically make a customer compliant with any law, regulation or certification standard. Actual
              compliance depends on the organisation's jurisdiction, scope, processes, data quality, professional
              judgement and independent verification.
            </p>
            <p className="text-sm font-medium text-[#0F3D3A] leading-relaxed mt-4">
              Our role is to provide the technology, structured data architecture, traceability, workflow, analytics and
              reporting that make these requirements significantly easier to understand, manage and evidence.
            </p>
          </div>
        </div>
      </section>

      {/* One data foundation (dark band) */}
      <section className="bg-[#0F3D3A] text-[#F2F4F3]" data-testid="about-foundation">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <Eyebrow onDark>One data foundation · many requirements</Eyebrow>
          <h2 className="text-3xl font-bold tracking-tight max-w-3xl" style={serif}>
            Never rebuild your sustainability data for every new questionnaire.
          </h2>
          <div className="mt-8 rounded-2xl bg-white/[0.05] border border-white/10 p-6 lg:p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F2F4F3]/50 mb-4">The Lestari principle</div>
            <FlowBar steps={DATA_FLOW} onDark testid="about-data-flow" />
          </div>
          <div className="grid lg:grid-cols-3 gap-4 mt-6" data-testid="about-reuse">
            {REUSE_EXAMPLES.map((r) => (
              <div key={r.source} className="rounded-xl bg-white/[0.05] border border-white/10 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Database className="h-4 w-4" style={{ color: GOLD }} />
                  <span className="text-sm font-semibold">{r.source}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {r.chain.map((c) => (
                    <span key={c} className="text-[11px] rounded-full px-2.5 py-1 bg-white/[0.06] border border-white/10 text-[#F2F4F3]/80">{c}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[#F2F4F3]/60 text-xs mt-6 max-w-3xl leading-relaxed font-mono">
            The real value of sustainability technology is not producing more forms — it is creating one trusted
            intelligence foundation from which many decisions can be made.
          </p>
        </div>
      </section>

      {/* Built to evolve */}
      <section className="bg-white border-t border-[#0F3D3A]/10" data-testid="about-evolve">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Eyebrow>Built to evolve</Eyebrow>
            <h2 className="text-3xl font-bold tracking-tight" style={serif}>Today's data, ready for tomorrow's requirements.</h2>
            <p className="text-[#0F3D3A]/70 text-sm mt-4 leading-relaxed max-w-md">
              Standards, regulations and stakeholder expectations keep changing. Lestari is an evolving platform, not a
              static reporting template. As frameworks develop, SusTek continuously reviews their implications across the
              platform — so organisations strengthen their capabilities without rebuilding their technology foundation.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3" data-testid="about-evolve-items">
            {EVOLVE_ITEMS.map((x) => (
              <div key={x} className="rounded-xl border border-[#0F3D3A]/10 bg-[#F2F4F3] p-4 flex items-center gap-2.5">
                <GitBranch className="h-4 w-4 shrink-0" style={{ color: TEAL }} />
                <span className="text-sm font-medium">{x}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advocacy */}
      <section className="border-t border-[#0F3D3A]/10" data-testid="about-advocacy">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <Eyebrow>Our advocacy</Eyebrow>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight max-w-4xl leading-tight" style={serif}>
            Sustainability transformation must become inclusive, practical and economically achievable.
          </h2>
          <p className="text-[#0F3D3A]/70 text-sm mt-5 max-w-3xl leading-relaxed">
            Asia does not need solutions designed only for organisations with enormous teams and budgets. It needs
            solutions that work for SMEs, that help organisations begin even when their data is incomplete, and that
            simplify complexity without sacrificing credibility. The transition to a sustainable economy cannot succeed
            if only the largest corporations can afford to participate.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-9">
            {[
              { icon: Sprout, t: "Sustainability must scale", b: "Accessible to organisations of every size and maturity." },
              { icon: ShieldCheck, t: "Resilience must be accessible", b: "Climate adaptation is becoming as important as mitigation." },
              { icon: Compass, t: "Technology makes both possible", b: "Connecting compliance with real business performance." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-[#0F3D3A]/10 bg-white p-5">
                <c.icon className="h-5 w-5 mb-3" style={{ color: TEAL }} />
                <div className="font-semibold text-sm">{c.t}</div>
                <p className="text-xs text-[#0F3D3A]/65 mt-1.5 leading-relaxed">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* More than ESG software */}
      <section className="bg-white border-t border-[#0F3D3A]/10" data-testid="about-capabilities">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <Eyebrow>More than ESG software</Eyebrow>
          <h2 className="text-3xl font-bold tracking-tight max-w-3xl" style={serif}>An evolving Sustainability &amp; Resilience Engine.</h2>
          <p className="text-[#0F3D3A]/65 text-sm mt-3 max-w-3xl leading-relaxed">
            Connecting the capabilities organisations need to become more sustainable today and more resilient for tomorrow.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="about-capability-grid">
            {CAPABILITIES.map((c) => (
              <div key={c.title} className="rounded-xl border border-[#0F3D3A]/10 bg-[#F2F4F3] p-5 hover:shadow-md transition-shadow" data-testid={`about-cap-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-4" style={{ background: "#117A7A14", border: "1px solid #117A7A33" }}>
                  <c.icon className="h-5 w-5" style={{ color: TEAL }} />
                </div>
                <div className="font-semibold text-sm">{c.title}</div>
                <div className="text-xs text-[#0F3D3A]/65 mt-1.5 leading-relaxed">{c.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why the name Lestari */}
      <section className="border-t border-[#0F3D3A]/10" data-testid="about-name">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <img src={LESTARI_IMG} alt="Enduring terraced landscape in Southeast Asia" loading="lazy"
              className="w-full h-[300px] lg:h-[380px] object-cover rounded-2xl border border-[#0F3D3A]/10 shadow-lg" data-testid="about-name-img" />
          </div>
          <div>
            <Eyebrow>Why the name Lestari?</Eyebrow>
            <h2 className="text-3xl font-bold tracking-tight" style={serif}>Something that endures — for generations.</h2>
            <p className="text-[#0F3D3A]/70 text-sm mt-5 leading-relaxed">
              <span className="font-semibold" style={{ color: TEAL }}>Lestari</span> is a word used across Southeast Asia to
              express something that endures, remains sustainable and continues for generations. That meaning reflects our
              vision perfectly.
            </p>
            <p className="text-[#0F3D3A]/70 text-sm mt-4 leading-relaxed">
              Sustainability should not be a campaign, a reporting season or a temporary corporate initiative. It should
              become part of how organisations operate, compete, adapt and endure.
            </p>
          </div>
        </div>
      </section>

      {/* Vision + network (dark band) */}
      <section className="bg-[#0F3D3A] text-[#F2F4F3]" data-testid="about-vision">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <Eyebrow onDark>Our vision</Eyebrow>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight max-w-4xl leading-tight" style={serif}>
            A future where every organisation in Asia — large or small — can access the intelligence to build a
            sustainable, resilient business.
          </h2>
          <p className="text-[#F2F4F3]/70 text-sm mt-5 max-w-3xl leading-relaxed">
            We envision thousands of businesses using shared sustainability intelligence to improve operations, strengthen
            communities, protect natural resources and collectively accelerate Asia's transition. No organisation
            transforms alone — which is why Lestari is not simply building a platform. We are building a network.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-8" data-testid="about-influence-chain">
            {INFLUENCE_CHAIN.map((x, i) => (
              <React.Fragment key={x}>
                <span className="text-xs sm:text-sm rounded-full px-3.5 py-1.5 bg-white/[0.06] border border-white/10">{x}</span>
                {i < INFLUENCE_CHAIN.length - 1 && <ChevronRight className="h-4 w-4" style={{ color: GOLD }} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Powered by SusTek + contact */}
      <section className="bg-white border-t border-[#0F3D3A]/10" data-testid="about-sustek">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14">
          <div>
            <Eyebrow>Powered &amp; managed by SusTek</Eyebrow>
            <h2 className="text-3xl font-bold tracking-tight" style={serif}>Wholly owned by SusTek Pte Ltd, Singapore.</h2>
            <p className="text-[#0F3D3A]/70 text-sm mt-5 leading-relaxed">
              As owner of the platform's intellectual property, SusTek is responsible for its product architecture,
              technology roadmap, sustainability methodologies, AI-enabled capabilities and continuing development. This
              ownership model keeps Lestari focused on its mission while continuously adapting to emerging standards,
              climate risks, regulatory expectations and the evolving needs of Asian organisations.
            </p>
          </div>
          <div className="rounded-2xl border border-[#0F3D3A]/10 bg-[#F2F4F3] p-6 lg:p-8" data-testid="about-contact">
            <div className="flex items-center gap-2.5 mb-5">
              <img src="/brand/logo_icon.png" alt="SusTek" className="h-7" />
              <span className="font-bold" style={serif}>SusTek Pte Ltd</span>
            </div>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3" data-testid="about-contact-address">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: TEAL }} />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#0F3D3A]/50">Registered office</div>
                  <div className="text-[#0F3D3A]/80 mt-0.5">Level 24-01, CapitaGreen, 138 Market Street, Singapore 048946</div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="about-contact-email">
                <Mail className="h-4 w-4 mt-0.5 shrink-0" style={{ color: TEAL }} />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#0F3D3A]/50">Enquiries</div>
                  <div className="mt-0.5">
                    <a href="mailto:contactus@sustek.ai" className="text-[#117A7A] hover:text-[#0F3D3A] transition-colors">contactus@sustek.ai</a>
                    <span className="text-[#0F3D3A]/40"> · </span>
                    <a href="tel:+6566796115" className="text-[#117A7A] hover:text-[#0F3D3A] transition-colors">+65 6679 6115</a>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="about-contact-web">
                <Globe className="h-4 w-4 mt-0.5 shrink-0" style={{ color: TEAL }} />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#0F3D3A]/50">Web</div>
                  <a href="https://sustek.ai" target="_blank" rel="noopener noreferrer" className="text-[#117A7A] hover:text-[#0F3D3A] transition-colors mt-0.5 inline-block">sustek.ai</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0F3D3A] text-[#F2F4F3]" data-testid="about-cta">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 text-center">
          <img src="/brand/logo_icon.png" alt="" className="h-14 mx-auto mb-5 brightness-110" />
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-4" style={{ color: GOLD }}>Join the Lestari Sustainability Network</div>
          <div className="max-w-2xl mx-auto space-y-1">
            {["Measure what matters.", "Understand what is changing.", "Act where it counts.", "Build resilience that lasts."].map((l) => (
              <div key={l} className="text-2xl sm:text-3xl font-bold tracking-tight" style={serif}>{l}</div>
            ))}
          </div>
          <p className="text-[#F2F4F3]/70 text-sm mt-6 max-w-xl mx-auto leading-relaxed">
            Whether you are beginning your journey, strengthening climate resilience, supporting suppliers, seeking
            financing or helping others transform — there is a place for you.
          </p>
          <button onClick={go} data-testid="about-cta-btn"
            className="mt-8 inline-flex items-center gap-2 font-bold text-sm px-7 py-3 rounded-full transition-colors shadow-lg"
            style={{ background: GOLD, color: INK }}>
            <ShieldCheck className="h-4 w-4" /> Start free trial <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-6 text-xs font-mono text-[#F2F4F3]/50">
            Lestari AI Platform · The Sustainability &amp; Resilience Engine for Asia · A wholly owned IP of SusTek Pte Ltd, Singapore.
          </p>
        </div>
      </section>

      {/* Branded footer nav */}
      <footer className="bg-[#0F3D3A] border-t border-white/10 text-[#F2F4F3]">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#F2F4F3]/60">
          <div className="flex items-center gap-2.5" data-testid="about-footer-sustek">
            <img src="/brand/logo_icon.png" alt="" className="h-5" />
            <span><span className="font-semibold text-[#F2F4F3]/90">LESTARI AI by SusTek</span> — Sustainability · Intelligence · Impact</span>
          </div>
          <div className="flex gap-5">
            <Link to="/about" className="hover:text-[#F2F4F3]" data-testid="about-footer-about">About</Link>
            <Link to="/why-lestari" className="hover:text-[#F2F4F3]">Why Lestari</Link>
            <Link to="/login" className="hover:text-[#F2F4F3]">Sign in</Link>
            <Link to="/get-started" className="hover:text-[#F2F4F3]">Start trial</Link>
          </div>
        </div>
      </footer>
      <Footer />
    </div>
  );
}
